/**
 * siteCssVars.js
 * ──────────────
 * ONE builder for the `--site-*` CSS custom properties that every block
 * reads (heading colors, fonts, buttons, prose). Used by:
 *   - Application/websites/cms/canvas/editorCanvas.vue  (editor canvas)
 *   - Website/vue/App.vue                                (public site)
 *   - Application/websites/cms/siteDesign/siteShowcase.vue (Site Design preview)
 * Keeping it in one place is what makes the three previews identical.
 *
 * Only `siteSettings` tokens live here. Page-level background overrides
 * are applied by the callers on top of this map.
 */

var HEADING_DEFAULTS = {
	h1: { size: '2.5em',  weight: '700', lineHeight: '1.2',  },
	h2: { size: '2em',    weight: '700', lineHeight: '1.25' },
	h3: { size: '1.75em', weight: '600', lineHeight: '1.3'  },
	h4: { size: '1.5em',  weight: '600', lineHeight: '1.35' },
	h5: { size: '1.25em', weight: '600', lineHeight: '1.4'  },
	h6: { size: '1em',    weight: '600', lineHeight: '1.45' }
};

export var SITE_DEFAULTS = {
	headingFont: 'Varela Round, sans-serif',
	bodyFont: 'Open Sans, sans-serif',
	baseFontSize: '16px',
	headingColor: '#1E2031',
	bodyColor: '#333333',
	linkColor: '#0080FF',
	accentColor: '#FB6A00',
	pageColor: '#FFFFFF',
	pageBackgroundColor: '#FFFFFF',
	pageWidth: '1000px'
};

/**
 * Builds the `--site-*` variable map plus base typography for a root element.
 *
 * @param {Object} s  siteSettings
 * @returns {Object} inline style object (CSS custom properties + font/color)
 */
export function buildSiteCssVars(s) {
	s = s || {};
	var style = {};

	style.fontFamily = s.bodyFont || SITE_DEFAULTS.bodyFont;
	style.fontSize = s.baseFontSize || SITE_DEFAULTS.baseFontSize;
	style.color = s.bodyColor || SITE_DEFAULTS.bodyColor;

	style['--site-page-color'] = s.pageColor || SITE_DEFAULTS.pageColor;
	style['--site-page-width'] = s.pageWidth || SITE_DEFAULTS.pageWidth;
	style['--site-heading-font'] = s.headingFont || SITE_DEFAULTS.headingFont;
	style['--site-heading-color'] = s.headingColor || SITE_DEFAULTS.headingColor;
	style['--site-body-font'] = s.bodyFont || SITE_DEFAULTS.bodyFont;
	style['--site-body-color'] = s.bodyColor || SITE_DEFAULTS.bodyColor;
	style['--site-link-color'] = s.linkColor || SITE_DEFAULTS.linkColor;
	style['--site-accent-color'] = s.accentColor || SITE_DEFAULTS.accentColor;

	// H1–H6. Non-color properties always get a default so a heading always
	// has a size/weight. Color is set ONLY when configured so the
	// `var(--site-h2-color, var(--site-heading-color))` chain resolves.
	Object.keys(HEADING_DEFAULTS).forEach(function (h) {
		var d = HEADING_DEFAULTS[h];
		style['--site-' + h + '-font'] = s[h + 'FontFamily'] || s.headingFont || SITE_DEFAULTS.headingFont;
		style['--site-' + h + '-size'] = s[h + 'FontSize'] || d.size;
		style['--site-' + h + '-weight'] = s[h + 'FontWeight'] || d.weight;
		style['--site-' + h + '-style'] = s[h + 'FontStyle'] || 'normal';
		style['--site-' + h + '-decoration'] = s[h + 'TextDecoration'] || 'none';
		style['--site-' + h + '-line-height'] = s[h + 'LineHeight'] || d.lineHeight;
		style['--site-' + h + '-letter-spacing'] = s[h + 'LetterSpacing'] || '0';
		style['--site-' + h + '-transform'] = s[h + 'TextTransform'] || 'none';
		if (s[h + 'Color']) style['--site-' + h + '-color'] = s[h + 'Color'];
	});

	// Buttons
	style['--site-btn-font'] = s.buttonFont || s.bodyFont || 'inherit';
	style['--site-btn-font-size'] = s.buttonFontSize || '1em';
	style['--site-btn-font-weight'] = s.buttonFontWeight || '600';
	style['--site-btn-radius'] = s.buttonBorderRadius || '4px';
	style['--site-btn-padding'] = (s.buttonPaddingVertical || '0.625rem') + ' ' + (s.buttonPaddingHorizontal || '1.5rem');
	style['--site-btn-transform'] = s.buttonTextTransform || 'none';
	style['--site-btn-primary-bg'] = s.buttonPrimaryBg || SITE_DEFAULTS.accentColor;
	style['--site-btn-primary-color'] = s.buttonPrimaryColor || '#FFFFFF';
	style['--site-btn-primary-border'] = s.buttonPrimaryBorderColor || s.buttonPrimaryBg || SITE_DEFAULTS.accentColor;
	style['--site-btn-secondary-bg'] = s.buttonSecondaryBg || '#FFFFFF';
	style['--site-btn-secondary-color'] = s.buttonSecondaryColor || '#1E2031';
	style['--site-btn-secondary-border'] = s.buttonSecondaryBorderColor || '#DDDEE0';

	// Blockquote
	style['--site-bq-font-size'] = s.blockquoteFontSize || '1.125em';
	style['--site-bq-font-style'] = s.blockquoteFontStyle || 'italic';
	style['--site-bq-line-height'] = s.blockquoteLineHeight || '1.6';
	style['--site-bq-color'] = s.blockquoteColor || '#555555';
	style['--site-bq-border-color'] = s.blockquoteBorderColor || SITE_DEFAULTS.accentColor;
	style['--site-bq-border-width'] = s.blockquoteBorderWidth || '4px';
	style['--site-bq-bg'] = s.blockquoteBgColor || '#FAFAFA';
	style['--site-bq-padding'] = s.blockquotePadding || '1rem 1.5rem';
	style['--site-bq-margin'] = s.blockquoteMargin || '1.5rem 0';

	// Inline code
	style['--site-code-font'] = s.codeFont || 'Fira Code, monospace';
	style['--site-code-font-size'] = s.codeFontSize || '0.875em';
	style['--site-code-color'] = s.codeColor || '#E31515';
	style['--site-code-bg'] = s.codeBgColor || '#F4F4F5';
	style['--site-code-radius'] = s.codeBorderRadius || '3px';
	style['--site-code-padding'] = s.codePadding || '0.15em 0.4em';

	// Code block
	style['--site-pre-font'] = s.codeBlockFont || 'Fira Code, monospace';
	style['--site-pre-font-size'] = s.codeBlockFontSize || '0.875em';
	style['--site-pre-color'] = s.codeBlockColor || '#E8E8E8';
	style['--site-pre-bg'] = s.codeBlockBgColor || '#1E2031';
	style['--site-pre-radius'] = s.codeBlockBorderRadius || '6px';
	style['--site-pre-padding'] = s.codeBlockPadding || '1rem 1.25rem';
	style['--site-pre-line-height'] = s.codeBlockLineHeight || '1.6';

	// Horizontal rule
	style['--site-hr-color'] = s.hrColor || '#DDDEE0';
	style['--site-hr-height'] = s.hrHeight || '1px';
	style['--site-hr-style'] = s.hrStyle || 'solid';
	style['--site-hr-margin'] = s.hrMargin || '2rem 0';

	// Lists
	style['--site-list-padding'] = s.listPaddingLeft || '1.5rem';
	style['--site-list-spacing'] = s.listItemSpacing || '0.35rem';
	style['--site-list-marker-color'] = s.listMarkerColor || SITE_DEFAULTS.accentColor;

	// Paragraphs
	style['--site-p-margin'] = s.paragraphMargin || '0 0 1rem';
	style['--site-p-line-height'] = s.paragraphLineHeight || '1.6';

	// Media corners: Post List thumbnails and Image blocks default to this.
	style['--site-image-radius'] = (s.imageRadius === undefined || s.imageRadius === null || s.imageRadius === '') ? '8px' : s.imageRadius;

	// Light / dark theme tokens (consumed by the Background & Theme trait)
	style['--site-light-bg'] = s.lightThemeBg || '#FFFFFF';
	style['--site-light-text'] = s.lightThemeText || '#1E2031';
	style['--site-light-accent'] = s.lightThemeAccent || SITE_DEFAULTS.accentColor;
	style['--site-dark-bg'] = s.darkThemeBg || '#1E2031';
	style['--site-dark-text'] = s.darkThemeText || '#F4F4F5';
	style['--site-dark-accent'] = s.darkThemeAccent || SITE_DEFAULTS.accentColor;

	return style;
}

/**
 * Older sites stored the toggle as true/false; the select stores '', 'full' or 'condensed'.
 * @returns {''|'full'|'condensed'}
 */
export function stickyHeaderMode(value) {
	if (value === 'condensed') return 'condensed';
	if (value === true || value === 'true' || value === 1 || value === '1' || value === 'full') return 'full';
	return '';
}
